import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import Alert from "../components/Alert";
import Button from "../components/Button";
import Modal from "../components/Modal";
import persoChallenges from "../libs/PersoChallenges";
import defaultIcon from "../assets/perso-categories/learning.png";

const CHALLENGES_LIFE_QUERY = gql`
query LifeChallenges {
  challenges: getLifeChallenges {
    id
    kpiName
    kpiUnit
    name
    icon
    isCustom
    actions {
      name
      id
    }
  }
}
`;

const PERSO_TEAMS_QUERY = gql`
query MyPersonalTeams {
  teams: myPersonalTeams {
    id
    name
  }
}
`;

const CREATE_PERSONAL_CATEGORY = gql`
  mutation CreatePersonalCategory($name: String!, $kpiName: String!, $kpiUnit: String!, $icon: String) {
    createPersonalCategory(name: $name, kpiName: $kpiName, kpiUnit: $kpiUnit, icon: $icon) {
      success
      message
      id
    }
  }
`;

function PersoSelectTargetsPage() {

  const [selectedCategories, setSelectedCategories] = React.useState<Challenge[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", kpiName: "", kpiUnit: "number" });

  const { data: currentTeamsData, loading: currentTeamsLoading } = useQuery(PERSO_TEAMS_QUERY)
  const { data: challengeData, loading: loadingTeams, refetch } = useQuery(CHALLENGES_LIFE_QUERY)
  const [createCategory] = useMutation(CREATE_PERSONAL_CATEGORY);
  const navigate = useNavigate();

  const challenges = useMemo(() => {
      const t =  challengeData?.challenges?.map((team: any) => ({
        ...team,
        ...(persoChallenges as any)[team.name],
        teamName: team.name,
      }));

      return t;
  }, [challengeData])

  useEffect(() => {
    if (challenges && currentTeamsData?.teams) {
      const currentChallenges = challenges?.filter((c:Challenge) => currentTeamsData?.teams?.find((team:Team) => team.name === c.teamName)).map((c:Challenge) => ({ ...c, new: false }));
      setSelectedCategories(currentChallenges || []);
    }
  }, [currentTeamsData, challenges]);

  function categoryClicked(challenge: Challenge) {
    if (selectedCategories.includes(challenge)) {
      setSelectedCategories(selectedCategories.filter((c) => c.id !== challenge.id));
    } else {
      setSelectedCategories([...selectedCategories, challenge]);
    }
  }

  async function sendSelected() {
    if (!selectedCategories.length) return alert("You must select at least one challenge");

    const data = JSON.parse(sessionStorage.getItem('formValue') || "{}");
    data.challenges = selectedCategories;
    sessionStorage.setItem('formValue', JSON.stringify(data));

    navigate('/perso/assign');
  }

  async function handleCreateCategory() {
    if (!newCategory.name.trim() || !newCategory.kpiName.trim()) {
      return alert("Please fill in all required fields");
    }

    try {
      await createCategory({
        variables: {
          name: newCategory.name,
          kpiName: newCategory.kpiName,
          kpiUnit: newCategory.kpiUnit,
        },
      });
      setShowCreateModal(false);
      setNewCategory({ name: "", kpiName: "", kpiUnit: "number" });
      refetch();
    } catch (e) {
      alert((e as Error).message);
    }
  }
  
  return (
    <div className="w-full p-4">
      <div className="flex pt-3 pb-6">
      <h1 className="text-3xl font-bold font-sans flex-1 lg:pl-4">
          Personal goals: Pick your personal challenges
        </h1>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 sm:relative sm:bg-transparent sm:border-0">
          {selectedCategories.length < 1 ? (
            <span>{`Select at least one personal challenge to continue`}</span>
          ) : (
            <>
              <Button onClick={sendSelected} className="block lg:hidden">Continue to your goals</Button>
              <Button onClick={sendSelected} className="hidden lg:block">Continue</Button>
            </>
          )}
        </div>
      </div>

      {(loadingTeams || currentTeamsLoading) ? (
        <Alert display type="info">
          Loading categories...
        </Alert>
      ) : (
        <div className="lg:grid lg:grid-cols-4 space-y-2 gap-4 pb-16 lg:pb-6">
          {!!challenges?.length && challenges.map((challenge: Challenge) => {
            const selected = selectedCategories.find((c) => c.id === challenge.id);
            return (
              <div
                className={`${
                  selected ? "border-blue-400" : "border-white"
                } border-4 p-6 bg-white w-full shadow-sm rounded-lg items-center justify-center flex lg:flex-col cursor-pointer hover:shadow-lg`}
                onClick={() => categoryClicked(challenge)}
                key={`team-perso${challenge.id}`}
              >
                <img 
                  src={challenge.icon || defaultIcon}
                  alt={challenge.name}
                  className="max-h-12 max-w-12 lg:max-h-28 lg:max-w-28" />
                <span className="text-lg text-gray-700 font-bold block p-4 text-center">
                  {challenge.name}
                </span>
                <span>
                  Def. KPI: <div className="inline-block">{challenge.kpiName}</div>
                </span>
              </div>
            );
          })}

          {/* Create Custom Category Card */}
          <div
            className="border-4 border-dashed border-gray-300 p-6 bg-white w-full shadow-sm rounded-lg items-center justify-center flex lg:flex-col cursor-pointer hover:shadow-lg hover:border-blue-300"
            onClick={() => setShowCreateModal(true)}
          >
            <div className="flex items-center justify-center w-12 h-12 lg:w-28 lg:h-28 bg-gray-100 rounded-full">
              <FaPlus className="text-2xl lg:text-4xl text-gray-400" />
            </div>
            <span className="text-lg text-gray-700 font-bold block p-4 text-center">
              Create Custom
            </span>
            <span className="text-gray-400 text-sm">
              Define your own category
            </span>
          </div>
        </div>

        )}

      {/* Create Custom Category Modal */}
      {showCreateModal && (
        <Modal
          title="Create Custom Category"
          closeFunction={() => setShowCreateModal(false)}
          autoHeight
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category Name *
              </label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                placeholder="e.g., Side Projects, Volunteering..."
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                KPI Name *
              </label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2"
                placeholder="e.g., # Hours, $ Amount, # Items..."
                value={newCategory.kpiName}
                onChange={(e) => setNewCategory({ ...newCategory, kpiName: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                KPI Unit
              </label>
              <select
                className="w-full border rounded px-3 py-2"
                value={newCategory.kpiUnit}
                onChange={(e) => setNewCategory({ ...newCategory, kpiUnit: e.target.value })}
              >
                <option value="number">Number</option>
                <option value="currency">Currency ($)</option>
                <option value="percentage">Percentage (%)</option>
                <option value="hours">Hours</option>
              </select>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outlined" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCategory}>
                Create Category
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default PersoSelectTargetsPage;
